'use client';
import React from 'react';

interface Supporter {
  id: number;
  name: string;
  abbr: string;
  category: string;
  image: string;
}

const supporters: Supporter[] = [
  { id: 1, name: 'Madhya Pradesh Tourism', abbr: 'MPT', category: 'Government', image: '' },
  { id: 2, name: 'Ministry of Culture', abbr: 'MoC', category: 'Government', image: '' },
  { id: 3, name: 'Indian Heritage Society', abbr: 'IHS', category: 'Heritage', image: '' },
  { id: 4, name: 'Archaeological Survey', abbr: 'ASI', category: 'Heritage', image: '' },
  { id: 5, name: 'Bhopal Municipal Corp', abbr: 'BMC', category: 'Civic', image: '' },
  { id: 6, name: 'Royal Arts Foundation', abbr: 'RAF', category: 'Arts', image: '' },
  { id: 7, name: 'Indore Chamber', abbr: 'ICC', category: 'Business', image: '' },
  { id: 8, name: 'Heritage Hotels India', abbr: 'HHI', category: 'Hospitality', image: '' },
  { id: 9, name: 'Narmada Cultural Trust', abbr: 'NCT', category: 'Culture', image: '' },
  { id: 10, name: 'Gwalior Arts Council', abbr: 'GAC', category: 'Arts', image: '' },
  { id: 11, name: 'Ujjain Mahotsav', abbr: 'UMF', category: 'Festival', image: '' },
  { id: 12, name: 'Khajuraho Festival', abbr: 'KDF', category: 'Festival', image: '' },
];

const scrollItems = [...supporters, ...supporters];

const categoryConfig: Record<string, { bg: string; text: string; border: string }> = {
  Government: { bg: 'rgba(107,26,42,0.25)', text: '#E8A0A8', border: 'rgba(107,26,42,0.5)' },
  Heritage:   { bg: 'rgba(26,42,74,0.25)',  text: '#A0B4E8', border: 'rgba(26,42,74,0.5)' },
  Civic:      { bg: 'rgba(26,58,42,0.25)',  text: '#A0D4B4', border: 'rgba(26,58,42,0.5)' },
  Arts:       { bg: 'rgba(58,26,74,0.25)',  text: '#C4A0E8', border: 'rgba(58,26,74,0.5)' },
  Business:   { bg: 'rgba(74,58,26,0.25)',  text: '#E8D4A0', border: 'rgba(74,58,26,0.5)' },
  Hospitality:{ bg: 'rgba(74,42,26,0.25)',  text: '#E8B4A0', border: 'rgba(74,42,26,0.5)' },
  Culture:    { bg: 'rgba(26,58,58,0.25)',  text: '#A0D4D4', border: 'rgba(26,58,58,0.5)' },
  Festival:   { bg: 'rgba(201,168,76,0.15)',text: '#C9A84C', border: 'rgba(201,168,76,0.4)' },
};

const LogoCard = ({ supporter }: { supporter: Supporter }) => {
  const cfg = categoryConfig[supporter.category] || categoryConfig.Festival;
  return (
    <div
      className="flex-shrink-0 flex items-center gap-4 px-6 py-5 rounded-2xl mx-3 cursor-default select-none"
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(201,168,76,0.14)',
        backdropFilter: 'blur(8px)',
        minWidth: '240px',
        transition: 'border-color 0.3s',
      }}
    >
      {/* Logo / Monogram circle */}
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${cfg.bg} 0%, rgba(201,168,76,0.08) 100%)`,
          border: `1px solid ${cfg.border}`,
        }}
      >
        {supporter.image ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={supporter.image}
            alt={`${supporter.name} logo`}
            className="w-full h-full object-contain p-1"
          />
        ) : (
          <span
            className="text-sm font-bold tracking-wide"
            style={{ color: cfg.text, fontFamily: 'DM Sans, sans-serif' }}
          >
            {supporter.abbr}
          </span>
        )}
      </div>

      {/* Text */}
      <div className="min-w-0">
        <p
          className="text-base font-semibold leading-tight truncate"
          style={{ color: '#FAF7F2', fontFamily: 'DM Sans, sans-serif', maxWidth: '150px' }}
        >
          {supporter.name}
        </p>
        <span
          className="inline-block text-xs mt-1.5 px-2.5 py-1 rounded-full font-medium"
          style={{
            background: cfg.bg,
            color: cfg.text,
            border: `1px solid ${cfg.border}`,
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '11px',
            letterSpacing: '0.04em',
          }}
        >
          {supporter.category}
        </span>
      </div>
    </div>
  );
};

export default function SupportedBySection() {
  return (
    <section
      className="relative py-20 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #0D0500 0%, #1A0A00 40%, #0D0500 100%)',
      }}
    >
      {/* Ambient glow blobs */}
      <div
        className="absolute top-0 left-1/4 w-96 h-64 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse, rgba(201,168,76,0.07) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />
      <div
        className="absolute bottom-0 right-1/4 w-96 h-64 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse, rgba(107,26,42,0.1) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />

      {/* Top divider */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.35), transparent)' }}
      />

      {/* ── Header ── */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-14 text-center">
        {/* Eyebrow */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="h-px w-12" style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.6))' }} />
          <span
            className="text-xs tracking-[0.3em] uppercase font-medium"
            style={{ color: '#C9A84C', fontFamily: 'DM Sans, sans-serif' }}
          >
            Our Patrons &amp; Partners
          </span>
          <div className="h-px w-12" style={{ background: 'linear-gradient(90deg, rgba(201,168,76,0.6), transparent)' }} />
        </div>

        <h2
          className="text-4xl md:text-5xl font-bold mb-4"
          style={{
            fontFamily: 'Fraunces, Georgia, serif',
            color: '#FAF7F2',
            letterSpacing: '-0.01em',
          }}
        >
          Supported By
        </h2>

        <p
          className="text-base max-w-xl mx-auto"
          style={{ color: 'rgba(250,247,242,0.5)', fontFamily: 'DM Sans, sans-serif', lineHeight: '1.7' }}
        >
          Proudly backed by government bodies, cultural institutions, and industry leaders
          who share our vision for India&apos;s heritage.
        </p>

        {/* Stat pills */}
        <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
          {[
            { value: '12+', label: 'Partners' },
            { value: '6', label: 'Categories' },
            { value: '5', label: 'States' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-2 px-4 py-2 rounded-full"
              style={{
                background: 'rgba(201,168,76,0.08)',
                border: '1px solid rgba(201,168,76,0.2)',
              }}
            >
              <span
                className="text-sm font-bold"
                style={{ color: '#C9A84C', fontFamily: 'Fraunces, Georgia, serif' }}
              >
                {stat.value}
              </span>
              <span
                className="text-xs"
                style={{ color: 'rgba(250,247,242,0.5)', fontFamily: 'DM Sans, sans-serif' }}
              >
                {stat.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Marquee Row 1 — left ── */}
      <div className="relative mb-4">
        <div
          className="absolute left-0 top-0 bottom-0 w-28 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(to right, #0D0500, transparent)' }}
        />
        <div
          className="absolute right-0 top-0 bottom-0 w-28 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(to left, #0D0500, transparent)' }}
        />
        <div
          className="flex"
          style={{ animation: 'marqueeLeft 38s linear infinite', width: 'max-content' }}
        >
          {scrollItems.map((s, i) => (
            <LogoCard key={`r1-${s.id}-${i}`} supporter={s} />
          ))}
        </div>
      </div>

      {/* ── Marquee Row 2 — right ── */}
      <div className="relative">
        <div
          className="absolute left-0 top-0 bottom-0 w-28 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(to right, #0D0500, transparent)' }}
        />
        <div
          className="absolute right-0 top-0 bottom-0 w-28 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(to left, #0D0500, transparent)' }}
        />
        <div
          className="flex"
          style={{ animation: 'marqueeRight 44s linear infinite', width: 'max-content' }}
        >
          {[...scrollItems].reverse().map((s, i) => (
            <LogoCard key={`r2-${s.id}-${i}`} supporter={s} />
          ))}
        </div>
      </div>

      {/* ── Category legend ── */}
      <div className="relative max-w-4xl mx-auto px-4 mt-12">
        <div
          className="rounded-2xl p-5"
          style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(201,168,76,0.1)',
          }}
        >
          <p
            className="text-center text-xs mb-4 tracking-widest uppercase"
            style={{ color: 'rgba(201,168,76,0.5)', fontFamily: 'DM Sans, sans-serif' }}
          >
            Partner Categories
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {Object.entries(categoryConfig).map(([cat, cfg]) => (
              <span
                key={cat}
                className="text-xs px-3 py-1.5 rounded-full font-medium"
                style={{
                  background: cfg.bg,
                  color: cfg.text,
                  border: `1px solid ${cfg.border}`,
                  fontFamily: 'DM Sans, sans-serif',
                  letterSpacing: '0.03em',
                }}
              >
                {cat}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
